import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Zap, Link as LinkIcon } from "lucide-react";
import { api } from "@/lib/api";
import { ShopBuyModal } from "@/components/ShopBuyModal";
import { productImg } from "@/lib/img";
import Seo from "@/components/Seo";
import { useKitAvailability } from "@/lib/kitAvailability";
import PAGES from "@/data/productPages.json";

const FILTERS = [
  { label: "All", value: "" },
  { label: "For Coaches", value: "coach" },
  { label: "For Parents", value: "parent" },
  { label: "For Players", value: "player" },
];

const AUDIENCE_BADGE = {
  coach: { label: "Coach", cls: "text-[#2D2D2D]" },
  parent: { label: "Parent", cls: "text-[#2E7D57]" },
  player: { label: "Player", cls: "text-[#DB9130]" },
};

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const BTN = "w-full sm:w-auto text-center bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-4 sm:px-5 py-2.5 hover:bg-[#D6922C] transition-colors";

function ProductCard({ p, kit, onBuy }) {
  const href = p.page || `/shop/${p.lookup_key}`;
  return (
    <div data-testid={`shop-card-${p.lookup_key}`} className="flex flex-col bg-[#E7E3D9] border-2 border-[#D3CFC0] hover:border-[#2D2D2D] transition-colors">
      <Link to={href} data-testid={`shop-card-link-${p.lookup_key}`} className="aspect-[4/3] overflow-hidden border-b-2 border-[#D3CFC0] bg-[#F3F1EC] flex items-center justify-center">
        <img {...productImg(p.image, "(min-width: 1024px) 33vw, 50vw")} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-contain p-3" />
      </Link>
      <div className="p-4 sm:p-6 flex flex-col flex-1">
        <div className="flex flex-wrap gap-2 mb-2">
          {p.is_bundle && <span className="kicker border border-current px-1.5 py-0.5 text-[#DB9130]">Bundle</span>}
          {p.audience.map((a) => {
            const b = AUDIENCE_BADGE[a] || { label: a, cls: "text-[#6E6E6E]" };
            return <span key={a} data-testid={`product-audience-${a}`} className={`kicker border border-current px-1.5 py-0.5 ${b.cls}`}>{b.label}</span>;
          })}
        </div>
        <h3 className="font-head font-semibold text-lg sm:text-xl text-[#1E1E1E] leading-tight"><Link to={href} className="hover:underline underline-offset-4">{p.name}</Link></h3>
        <p className="mt-2 text-[#6E6E6E] text-sm leading-relaxed flex-1">{p.blurb}</p>
        {p.checkout === "page" && kit.label && (
          <p className="mt-2 text-xs text-[#3A3A3A]" data-testid="shop-starter-kit-availability">
            <b>Available now:</b> {kit.label}. <Link to="/starter-kit#notify" className="underline underline-offset-2" data-testid="shop-starter-kit-notify">Other sport? Join the waitlist</Link>
          </p>
        )}
        <div className="mt-5 pt-4 border-t border-[#D3CFC0] flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="font-head text-2xl sm:text-3xl text-[#1E1E1E]">{p.free ? "Free" : `$${p.amount / 100}`}</span>
          {p.free
            ? <Link to="/cheat-sheet" data-testid={`shop-buy-${p.lookup_key}`} className={BTN}>Get it free</Link>
            : p.checkout === "page"
            ? <Link to={href} data-testid={`shop-buy-${p.lookup_key}`} className={BTN}>Build my kit</Link>
            : <button onClick={() => onBuy(p)} data-testid={`shop-buy-${p.lookup_key}`} className={`${BTN} disabled:opacity-60`}>Buy Now</button>}
        </div>
      </div>
    </div>
  );
}

function QuickPlayCard({ q }) {
  const to = `/quick-plays/${q.slug}`;
  return (
    <div data-testid={`shop-card-quick-play-${q.slug}`} className="flex flex-col bg-[#E7E3D9] border-2 border-[#D3CFC0] hover:border-[#2D2D2D] transition-colors">
      <Link to={to} className="aspect-[4/3] overflow-hidden border-b-2 border-[#D3CFC0] bg-[#1E1E1E] flex items-center justify-center p-4">
        <span className="font-head uppercase text-[#E8A33D] text-3xl sm:text-4xl leading-[0.9] text-center">{(q.title_stacked || [q.title]).map((t) => <span key={t} className="block">{t}</span>)}</span>
      </Link>
      <div className="p-4 sm:p-6 flex flex-col flex-1">
        <div className="flex flex-wrap gap-2 mb-2"><span className="kicker border border-current px-1.5 py-0.5 text-[#DB9130] inline-flex items-center gap-1"><Zap size={11} /> Quick Play</span></div>
        <h3 className="font-head font-semibold text-lg sm:text-xl text-[#1E1E1E] leading-tight"><Link to={to} className="hover:underline underline-offset-4">{q.title}</Link></h3>
        <p className="mt-2 text-[#6E6E6E] text-sm leading-relaxed flex-1">{q.short_description}</p>
        <div className="mt-5 pt-4 border-t border-[#D3CFC0] flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="font-head text-2xl sm:text-3xl text-[#1E1E1E]">${(q.price_cents / 100).toFixed(2)}</span>
          <Link to={to} data-testid={`shop-quick-play-link-${q.slug}`} className={BTN}>See it</Link>
        </div>
      </div>
    </div>
  );
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [quickPlays, setQuickPlays] = useState([]);
  const kit = useKitAvailability();
  const [filter, setFilter] = useState("");
  const [copied, setCopied] = useState(false);
  const idx = PAGES.situations.findIndex((s) => slugify(s.label) === params.get("situation"));
  const situation = idx >= 0 ? idx : null;
  const setSituation = (i) => {
    setCopied(false);
    const next = new URLSearchParams(params);
    if (i === null) next.delete("situation"); else next.set("situation", slugify(PAGES.situations[i].label));
    setParams(next, { replace: true });
  };
  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href).then(() => setCopied(true)).catch(() => {});
  };
  const [active, setActive] = useState(null);
  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data || []));
    api.get("/quick-plays").then(({ data }) => setQuickPlays(data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (situation === null) return;
    document.querySelector(`[data-testid="shop-situation-${slugify(PAGES.situations[situation].label)}"]`)
      ?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [situation]);

  const singles = products.filter((p) => !p.is_bundle && !p.line);
  const bundles = products.filter((p) => p.is_bundle && !p.line);
  const tmhf = products.filter((p) => p.line === "tmhf");
  const shown = singles.filter((p) => !filter || p.audience.includes(filter));
  const sit = situation !== null ? PAGES.situations[situation] : null;
  const sitItems = sit ? sit.products.map((k) => (k.startsWith("quick-plays/")
    ? { quick: quickPlays.find((q) => q.slug === k.split("/")[1]) }
    : { product: products.find((p) => p.lookup_key === k) })).filter((x) => x.quick || x.product) : [];
  const pickFilter = (v) => { setSituation(null); setFilter(v); };
  const pickSituation = (i) => { setFilter(""); setSituation(situation === i ? null : i); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-testid="shop-page">
      <Seo title="Shop — Digital Products" description="Toolkits, planners, and playbooks for youth-sports coaches and parents — instant digital downloads." path="/shop" />
      <span className="kicker">Shop</span>
      <h1 className="mt-2 font-head font-semibold uppercase text-4xl lg:text-6xl text-[#1E1E1E] leading-[1.05]">Digital Products</h1>
      <p className="mt-3 text-[#6E6E6E] max-w-2xl text-lg">
        Toolkits, planners, and playbooks for coaches and parents — instant digital downloads, no fluff.
      </p>

      <div className="mt-8" data-testid="shop-situations">
        <span className="kicker text-[#DB9130]">Shop by situation</span>
        <div className="mt-3 flex gap-2 overflow-x-auto snap-x pb-2">
          {PAGES.situations.map((s, i) => (
            <button key={s.label} onClick={() => pickSituation(i)} data-testid={`shop-situation-${slugify(s.label)}`} aria-pressed={situation === i}
              className={`shrink-0 snap-start whitespace-nowrap px-4 py-2.5 font-head uppercase tracking-wide text-xs border-2 transition-colors ${situation === i ? "border-[#E8A33D] bg-[#E8A33D] text-[#1A1A1A]" : "border-[#2D2D2D] text-[#2D2D2D] hover:bg-[#2D2D2D] hover:text-[#F3F1EC]"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2 border-b-2 border-[#D3CFC0] pb-4">
        {FILTERS.map((f) => (
          <button key={f.value} onClick={() => pickFilter(f.value)} data-testid={`shop-filter-${f.value || "all"}`}
            className={`px-4 py-2 font-head uppercase tracking-wide text-xs border-2 transition-colors ${!sit && filter === f.value ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {sit && (
        <p className="mt-6 text-sm text-[#3A3A3A]" data-testid="shop-situation-active">
          Showing: <b>{sit.label}</b>. <button onClick={() => setSituation(null)} className="underline underline-offset-2" data-testid="shop-situation-clear">Show everything</button>
          <button onClick={copyLink} className="ml-3 inline-flex items-center gap-1 underline underline-offset-2" data-testid="shop-situation-copy-link">
            <LinkIcon size={13} /> {copied ? "Link copied" : "Copy link to this list"}
          </button>
        </p>
      )}

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="shop-grid">
        {sit
          ? sitItems.map((x) => (x.quick ? <QuickPlayCard key={x.quick.slug} q={x.quick} /> : <ProductCard key={x.product.lookup_key} p={x.product} kit={kit} onBuy={setActive} />))
          : shown.map((p) => <ProductCard key={p.lookup_key} p={p} kit={kit} onBuy={setActive} />)}
      </div>
      <p className="mt-8 text-xs text-[#6E6E6E]">Secure checkout via Stripe. Receipts and downloads are emailed after purchase.</p>

      {!sit && bundles.length > 0 && (
        <div className="mt-16" data-testid="shop-bundles">
          <span className="kicker text-[#DB9130]">Bundle &amp; Save</span>
          <h2 className="mt-2 font-head font-semibold uppercase text-3xl lg:text-4xl text-[#1E1E1E] leading-[1.05]">Collections &amp; The Full Library</h2>
          <p className="mt-3 text-[#6E6E6E] max-w-2xl">Get a whole topic from every angle — coach, parent, and player — or grab the entire library at once.</p>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {bundles.map((b) => (
              <div key={b.lookup_key} data-testid={`shop-card-${b.lookup_key}`}
                className="flex flex-col bg-[#E7E3D9] border-2 border-[#DB9130]/60 hover:border-[#2D2D2D] transition-colors">
                <Link to={`/shop/${b.lookup_key}`} data-testid={`shop-card-link-${b.lookup_key}`} className="aspect-[4/3] overflow-hidden border-b-2 border-[#D3CFC0] bg-[#F3F1EC] flex items-center justify-center">
                  <img {...productImg(b.image, "(min-width: 1024px) 25vw, 50vw")} alt={b.name} loading="lazy" decoding="async" className="w-full h-full object-contain p-3" />
                </Link>
                <div className="p-4 sm:p-6 flex flex-col flex-1">
                  <span className="kicker text-[#DB9130]" data-testid={`bundle-badge-${b.lookup_key}`}>Bundle</span>
                  <h3 className="mt-1 font-head font-semibold text-base sm:text-lg text-[#1E1E1E] leading-tight"><Link to={`/shop/${b.lookup_key}`} className="hover:underline underline-offset-4">{b.name}</Link></h3>
                  <p className="mt-2 text-[#6E6E6E] text-sm leading-relaxed">{b.blurb}</p>
                  {Array.isArray(b.includes) && b.includes.length > 0 && (
                    <div className="mt-3 flex-1" data-testid={`bundle-includes-${b.lookup_key}`}>
                      <span className="kicker text-[#6E6E6E]">What's Inside</span>
                      <ul className="mt-1.5 space-y-1">
                        {b.includes.map((title) => (
                          <li key={title} className="flex items-start gap-2 text-sm text-[#1E1E1E]">
                            <Check size={14} className="mt-0.5 shrink-0 text-[#DB9130]" />
                            <span>{title}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="mt-5 pt-4 border-t border-[#D3CFC0] flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <span className="font-head text-2xl sm:text-3xl text-[#1E1E1E]">${b.amount / 100}</span>
                    <button onClick={() => setActive(b)} data-testid={`shop-buy-${b.lookup_key}`}
                      className={`${BTN} disabled:opacity-60`}>
                      Buy Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!sit && tmhf.length > 0 && (
        <div className="mt-16" data-testid="shop-tmhf">
          <span className="kicker text-[#DB9130]">New</span>
          <h2 className="mt-2 font-head font-semibold uppercase text-3xl lg:text-4xl text-[#1E1E1E] leading-[1.05]">Teach Me How to Football</h2>
          <p className="mt-3 text-[#6E6E6E] max-w-2xl">Football explained from zero, for parents, players and brand-new coaches. <Link to="/teach-me-how-to-football" className="underline underline-offset-4 text-[#1E1E1E]" data-testid="shop-tmhf-link">See the whole series</Link></p>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {tmhf.filter((p) => !filter || p.audience.includes(filter)).map((p) => <ProductCard key={p.lookup_key} p={p} kit={kit} onBuy={setActive} />)}
          </div>
        </div>
      )}

      {active && <ShopBuyModal product={active} onClose={() => setActive(null)} />}
    </div>
  );
}
